import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext();

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from localStorage on component mount
  useEffect(() => {
    const loadUser = () => {
      try {
        const savedUser = localStorage.getItem('cartoon_movie_studio_user');
        if (savedUser) {
          const user = JSON.parse(savedUser);
          dispatch({ type: 'SET_USER', payload: user });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Error loading user:', error);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    
    loadUser();
  }, []);

  // Save user to localStorage
  const saveUserToStorage = (user) => {
    try {
      localStorage.setItem('cartoon_movie_studio_user', JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  // Remove user from localStorage
  const removeUserFromStorage = () => {
    try {
      localStorage.removeItem('cartoon_movie_studio_user');
    } catch (error) {
      console.error('Error removing user:', error);
    }
  };

  const signup = async (name, email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create new user
      const newUser = {
        user_id: uuidv4(),
        name: name.trim(),
        email: email.toLowerCase().trim(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Save to localStorage (simulating database)
      saveUserToStorage(newUser);
      
      dispatch({ type: 'SET_USER', payload: newUser });
      return { success: true, user: newUser };
      
    } catch (error) {
      const errorMessage = 'Failed to create account. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw new Error(errorMessage);
    }
  };

  const login = async (email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check localStorage for existing user (simulating database lookup)
      const savedUser = localStorage.getItem('cartoon_movie_studio_user');
      
      if (savedUser) {
        const user = JSON.parse(savedUser);
        
        // Simple email validation (in real app, you'd check password hash)
        if (user.email === email.toLowerCase().trim()) {
          // Update last login
          const updatedUser = {
            ...user,
            updated_at: new Date().toISOString()
          };
          
          saveUserToStorage(updatedUser);
          dispatch({ type: 'SET_USER', payload: updatedUser });
          return { success: true, user: updatedUser };
        }
      }
      
      // If no user found or email doesn't match
      throw new Error('Invalid email or password');
      
    } catch (error) {
      const errorMessage = error.message || 'Login failed. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const logout = () => {
    removeUserFromStorage();
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const updateProfile = async (updates) => {
    if (!state.user) {
      throw new Error('No user logged in');
    }
    
    try {
      const updatedUser = {
        ...state.user,
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      saveUserToStorage(updatedUser);
      dispatch({ type: 'SET_USER', payload: updatedUser });
      return { success: true, user: updatedUser };
      
    } catch (error) {
      const errorMessage = 'Failed to update profile. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw new Error(errorMessage);
    }
  };

  const value = {
    ...state,
    signup,
    login,
    logout,
    clearError,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};



